import { ViewEdit } from './../model/viewEdit';
import { Component, OnInit } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { ViewService } from 'src/app/core/service/view.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/core/service/user.service';

@Component({
  standalone: false,
  selector: 'app-view-list',
  templateUrl: './view-list.component.html',
  styleUrls: ['./view-list.component.scss']
})
export class ViewListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: ViewEdit;
  public selectedView: ViewEdit;
  public filterText = '';
  public viewTypes = null;
  public reportText = '';
  public metricText = '';

  constructor(private viewService: ViewService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private translate: TranslateService,
  private userService: UserService,)
  {
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.viewTypes = this.viewService.viewTypes;
    this.translate.get(['REPORT-TYPE-REPORT', 'REPORT-TYPE-METRIC']).subscribe({
      next: (res) =>
      {
        this.reportText = res['REPORT-TYPE-REPORT'];
        this.metricText = res['REPORT-TYPE-METRIC'];
      }
    });
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const viewId = this.selectedView.id;
      this.viewService.deleteView(viewId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== viewId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('VIEW-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedView = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number, viewName: string): void
  {
    // Check if in use
    this.viewService.viewUsedInExtract(viewName).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('VIEW-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.viewService.getViewById(id).subscribe({
            next: (data) =>
            {
              this.selectedView = data as ViewEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new ViewEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.viewService.getViewById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(view: ViewEdit): void
  {
    this.viewService.saveView(view, this.isNew, view.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        this.loadGridData();
        this.messageHandlerService.displaySuccess('VIEW-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.viewService.getViewList().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.lastModifiedOn = new Date(d.lastModifiedOn);
          d.authorizedRolesText = d.authorizedRoles && d.authorizedRoles.length > 0 ?
            this.userService.convertRolesToDisplayTexts(d.authorizedRoles.join(',')) : '';
        });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
